/**
 * A person's signature block (0239): the lines printed under their name on
 * the assessment, the letter and the SPICE assessment. A box of a few lines,
 * saved on leaving it when the text changed; blank clears it, and the name
 * then prints alone. The office edits anyone's (the People page and the Team
 * person drawer); a person edits their own (the Team person drawer). The
 * server decides which rows a seat may save.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TextArea } from '@/components/primitives/TextInput';
import { FLD_TEXTAREA_GROW } from '@/components/primitives/modalFormStyles';
import { staffAdminApi } from '@/lib/staffAdmin';
import { teamDirectoryQueryKey } from '@/lib/teamDirectory';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export function SignatureBlockField({
  tenantSlug,
  rosterId,
  name,
  value,
  testId,
}: {
  tenantSlug: string;
  rosterId: string;
  name: string | null;
  value: string | null;
  testId: string;
}): ReactNode {
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const [text, setText] = useState(value ?? '');
  const [saved, setSaved] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (block: string) => staffAdminApi.setSignatureBlock(tenantSlug, rosterId, block),
    onSuccess: (_r, block) => {
      setSaved(block);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'staff', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'staff-profiles', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: teamDirectoryQueryKey(tenantSlug) });
    },
    onError: (e: unknown) => setError(pageWriteText(e, contact)),
  });
  const id = `signature-block-${rosterId}`;
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-ink-soft" htmlFor={id}>
        Signature block
      </label>
      <TextArea
        id={id}
        grow
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text.trim() !== saved.trim()) save.mutate(text);
        }}
        placeholder="Printed under the name on assessments and letters, for example a title."
        data-testid={testId}
        aria-describedby={`${id}-help`}
        aria-label={`Signature block for ${name ?? 'this person'}`}
        className={FLD_TEXTAREA_GROW}
      />
      <p id={`${id}-help`} className="text-xs text-ink-soft">
        Up to six lines. Left empty, documents are signed with the name alone.
      </p>
      {error !== null && (
        <p role="alert" className="text-xs text-pepper">
          {error}
        </p>
      )}
    </div>
  );
}
